import React, { useState } from "react";

// THE SYSTEM RESET SCENE
const SCENE = {
  meta: {
    id: "child-room-system-reset",
    title: "The 150-Week Problem",
    topic: "Parenting & Systems",
  },
  cast: {
    speaker: { name: "Parent", role: "Speaker" },
    listener: { name: "Sarah", role: "Child (12)" } // <--- UPDATED NAME
  },
  context: {
    summary: "After months of fighting about a messy room, Parent confronts Child. The realization: the issue isn't defiance, it's friction.",
    timeline: [
      "Weeks 1–150: Repeated reminders, frustration, messy room.",
      "Today: Parent confronts issue directly.",
      "Pivot: Parent realizes 'rules exist but systems do not'."
    ]
  },
  steps: {
    step0_contact: {
      speakerSays: "Every single element of the filth in this room needs to be addressed. This can be easy or painful — you decide.",
      listenerHears: "⚠ GLOBAL FAILURE / SHAME DETECTED"
    },
    step1_internalDiagnostics: {
      listenerStory: "I'm bad at this. I'm always in trouble. This feels overwhelming.",
      listenerSensation: "Shutdown, avoidance, low motivation.",
      listenerUrge: "Apologize quickly, promise vaguely, escape."
    },
    step2_externalForensics: {
      objectiveFacts: "Trash accumulates after eating. No visible trash can. Rules exist, systems do not.",
      wrapper: {
        tone: "Frustrated / Absolute",
        risk: "Turning habit failure into identity failure."
      },
      coreNeed: {
        speaker: "I need cleanliness without constant enforcement.",
        listener: "I need a way to succeed that doesn't feel overwhelming."
      }
    },
    step3_rewrite: {
      defaultReaction: "Detail out how. I don’t believe it right now.",
      wiseResponse: "Thank you for being specific. That tells me you’re trying. Let’s make this easier to succeed at."
    },
    step4_resolution: {
      outcome: "Child proposes actions. Parent reinforces effort. Conflict de-escalates."
    },
    step5_upgrade: {
      patternName: "Friction → Failure → Shame → Avoidance",
      coreTruth: "At 12, habits fail because systems fail — not because values are missing.",
      
      coachSummary: [
        "Children don't internalize values through lectures.",
        "They internalize values through repeated success.",
        "Environment design beats discipline every time."
      ],

      oneOnOne: {
        speaker: {
          hardTruth: "When everything is a lecture, kids stop listening — and stop trying.",
          focus: "Remove shame. Engineer ease. One rule, one outcome."
        },
        listener: {
          hardTruth: "You're not bad — the setup was bad.",
          focus: "Organization is a tool for freedom, not approval."
        }
      },

      togetherPlan: {
        environmentChange: "Install a motion-sensor trash can where trash naturally accumulates.",
        rule: "No food or food trash left in the bedroom overnight.",
        outcome: "If found: Bedroom food privilege paused for 48 hours. (Automatic. No lectures.)"
      },

      experiment7Day: {
        successMetric: "Less conflict. Child experiences autonomy. Parent experiences relief."
      }
    }
  }
};

export default function ChildRoomCard() {
  const [step, setStep] = useState(0);
  const [activeTab, setActiveTab] = useState("summary");

  const nextStep = () => setStep((s) => s + 1);
  const reset = () => {
    setStep(0);
    setActiveTab("summary");
  };

  // STYLE CONFIG
  const cardBg = "#1E293B";
  const textColor = "#F1F5F9";
  const mutedColor = "#94A3B8";
  const borderColor = "#475569";
  const accentColor = "#06B6D4"; // Cyan for Systems/Logic

  return (
    <section
      className="card lg section"
      style={{
        position: "relative",
        background: cardBg,
        color: textColor,
        border: `1px solid ${borderColor}`,
        overflow: "hidden",
        padding: 0,
        marginTop: 24,
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.5)"
      }}
    >
      {/* HEADER */}
      <div style={{ background: "rgba(0,0,0,0.3)", padding: "12px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${borderColor}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: step === 5 ? accentColor : "#F59E0B" }} />
          <span className="xs" style={{ fontFamily: "monospace", letterSpacing: "0.1em", color: mutedColor, fontSize: 11 }}>
             SCENE 03 // {SCENE.meta.title.toUpperCase()}
          </span>
        </div>
        {step > 0 && <button onClick={reset} className="xs" style={{ background: "transparent", border: `1px solid ${borderColor}`, color: mutedColor, padding: "2px 8px", borderRadius: 4, cursor: "pointer" }}>RESET</button>}
      </div>

      {/* CAST CONTEXT */}
      <div style={{ background: "#0F172A", padding: "8px 20px", borderBottom: `1px solid ${borderColor}`, display: 'flex', gap: 24 }}>
        <div className="xs" style={{ color: mutedColor }}>PARENT: <span style={{ color: "#FCA5A5", fontWeight: 700 }}>{SCENE.cast.speaker.name}</span></div>
        <div className="xs" style={{ color: mutedColor }}>CHILD: <span style={{ color: "#93C5FD", fontWeight: 700 }}>{SCENE.cast.listener.name}</span></div>
      </div>

      {/* STAGE */}
      <div style={{ padding: "32px 24px", minHeight: 460, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        
        {/* STEP 0 */}
        {step === 0 && (
          <div className="animate-in" style={{ textAlign: "center" }}>
            <p className="xs" style={{color: mutedColor, marginBottom: 16}}>CONTEXT: {SCENE.context.summary}</p>
            <div style={{ marginBottom: 32, textAlign: 'left', background: '#334155', borderRadius: '12px 12px 12px 0', padding: 20, maxWidth: '90%', margin: '0 auto 32px' }}>
               <div className="xs" style={{ color: "#FCA5A5", fontWeight: 700, marginBottom: 8 }}>{SCENE.cast.speaker.name} SAYS:</div>
               <div style={{ fontFamily: 'serif', fontSize: '1.25rem', fontStyle: 'italic', color: '#fff' }}>"{SCENE.steps.step0_contact.speakerSays}"</div>
            </div>
            <div style={{ borderTop: `1px dashed ${borderColor}`, paddingTop: 24 }}>
               <div className="xs" style={{ color: "#93C5FD", fontWeight: 700, marginBottom: 8 }}>{SCENE.cast.listener.name} HEARS:</div>
               <div className="xs" style={{ color: "#EF4444", fontWeight: 700, background: 'rgba(239, 68, 68, 0.1)', display: 'inline-block', padding: '6px 12px', borderRadius: 4, border: '1px solid #EF4444' }}>{SCENE.steps.step0_contact.listenerHears}</div>
            </div>
            <button onClick={nextStep} style={{ marginTop: 32, background: "#EF4444", color: "white", border: "none", padding: "14px 28px", fontWeight: 700, borderRadius: 50, cursor: "pointer" }}>⏸ PAUSE REACTION</button>
          </div>
        )}

        {/* STEP 1 */}
        {step === 1 && (
          <div className="animate-in">
            <h3 className="h3" style={{ color: textColor, textAlign: 'center', margin: "0 0 8px" }}>Internal Diagnostics</h3>
             <div style={{ background: "rgba(0,0,0,0.3)", borderRadius: 8, padding: 16, border: "1px dashed #F59E0B", marginBottom: 24 }}>
              <div className="xs" style={{ color: "#F59E0B", fontWeight: 700, marginBottom: 12 }}>CHILD'S SYSTEM_LOGS</div>
              <ul className="xs" style={{ color: "#FCA5A5", fontFamily: 'monospace', listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{marginBottom: 6}}>{`> STORY: "${SCENE.steps.step1_internalDiagnostics.listenerStory}"`}</li>
                <li style={{marginBottom: 6}}>{`> SENSATION: ${SCENE.steps.step1_internalDiagnostics.listenerSensation}`}</li>
                <li style={{color: '#EF4444'}}>{`> URGE: ${SCENE.steps.step1_internalDiagnostics.listenerUrge}`}</li>
              </ul>
            </div>
            <div style={{ textAlign: "center" }}>
              <button onClick={nextStep} className="pill" style={{ background: "#F59E0B", color: "#451A03", borderColor: "#F59E0B", fontWeight: 600 }}>See Past The Shutdown →</button>
            </div>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div className="animate-in">
            <h3 className="h3" style={{ color: textColor, textAlign: 'center', margin: "0 0 24px" }}>Forensics</h3>
            <div style={{ background: "rgba(6, 182, 212, 0.1)", border: `1px solid ${accentColor}`, padding: 12, borderRadius: 8, marginBottom: 16 }}>
              <div className="xs" style={{ color: accentColor, fontWeight: 700 }}>FACTS ONLY:</div>
              <div className="sm" style={{ color: "#CFFAFE" }}>{SCENE.steps.step2_externalForensics.objectiveFacts}</div>
            </div>
             <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div style={{ background: "rgba(239, 68, 68, 0.05)", border: "1px solid rgba(239, 68, 68, 0.4)", padding: 16, borderRadius: 12 }}>
                <div className="xs" style={{ color: "#FCA5A5", fontWeight: 700, marginBottom: 4 }}>WRAPPER</div>
                <div className="xs" style={{ color: "#FECACA" }}>
                  <strong>Tone:</strong> {SCENE.steps.step2_externalForensics.wrapper.tone}<br/>
                  <strong>Risk:</strong> {SCENE.steps.step2_externalForensics.wrapper.risk}
                </div>
              </div>
              <div style={{ background: "rgba(6, 182, 212, 0.1)", border: `1px solid ${accentColor}`, padding: 16, borderRadius: 12 }}>
                <div className="xs" style={{ color: accentColor, fontWeight: 700, marginBottom: 4 }}>CORE NEEDS</div>
                <div className="xs" style={{ color: "#CFFAFE" }}>
                  <strong>Parent:</strong> {SCENE.steps.step2_externalForensics.coreNeed.speaker}<br/>
                  <strong>Child:</strong> {SCENE.steps.step2_externalForensics.coreNeed.listener}
                </div>
              </div>
            </div>
            <div style={{ textAlign: "center", marginTop: 24 }}>
              <button onClick={nextStep} className="pill" style={{ background: "transparent", color: accentColor, borderColor: accentColor }}>Switch to Systems Mode →</button>
            </div>
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <div className="animate-in">
            <h3 className="h3" style={{ color: textColor, textAlign: 'center', margin: "0 0 24px" }}>Script Editor</h3>
            <div style={{ margin: "32px 0", textAlign: "left" }}>
               <div style={{ opacity: 0.4, marginBottom: 16, fontFamily: 'monospace' }} className="sm">
                <span className="xs" style={{color: '#EF4444', display: 'block'}}>OLD SCRIPT (DISTRUST):</span> 
                "{SCENE.steps.step3_rewrite.defaultReaction}"
              </div>
              <div style={{ background: "rgba(6, 182, 212, 0.1)", borderLeft: `4px solid ${accentColor}`, padding: 16, borderRadius: "0 8px 8px 0", color: "#67E8F9", fontFamily: 'monospace', fontSize: '1.0rem' }}>
                <span className="xs" style={{color: accentColor, fontWeight: 700, display: 'block'}}>NEW SCRIPT (SUPPORT):</span> 
                "{SCENE.steps.step3_rewrite.wiseResponse}"
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <button onClick={nextStep} className="pill" style={{ background: accentColor, color: "#0E7490", borderColor: accentColor, fontWeight: 700 }}>Execute →</button>
            </div>
          </div>
        )}

        {/* STEP 4 */}
        {step === 4 && (
           <div className="animate-in" style={{ textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✨</div>
            <h3 className="h3" style={{ color: textColor, margin: "0 0 16px" }}>Resolution</h3>
            <p className="sm" style={{ color: mutedColor, maxWidth: 400, margin: "0 auto 24px" }}>{SCENE.steps.step4_resolution.outcome}</p>
            <button onClick={nextStep} className="pill" style={{ background: "transparent", color: accentColor, borderColor: accentColor }}>Design The System →</button>
          </div>
        )}

        {/* STEP 5: UPGRADE */}
        {step === 5 && (
          <div className="animate-in">
            <h3 className="h3" style={{ color: accentColor, textAlign: 'center', margin: "0 0 4px" }}>System Upgrade</h3>
            <p className="xs" style={{ color: mutedColor, textAlign: 'center', marginBottom: 20 }}>Designing for success, not shame.</p>

             <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: `1px solid ${borderColor}`, paddingBottom: 16, overflowX: 'auto' }}>
              {[{ id: "summary", label: "PATTERN" }, { id: "spk", label: "PARENT 1:1" }, { id: "lis", label: "CHILD 1:1" }, { id: "plan", label: "THE SYSTEM" }].map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ background: activeTab === tab.id ? "rgba(255,255,255,0.1)" : "transparent", color: activeTab === tab.id ? "#fff" : mutedColor, border: "none", borderRadius: 4, padding: "6px 12px", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>{tab.label}</button>
              ))}
            </div>

            <div style={{ minHeight: 180 }}>
              {activeTab === "summary" && (
                <div className="animate-in">
                  <div style={{ background: `rgba(6, 182, 212, 0.1)`, borderLeft: `4px solid ${accentColor}`, padding: 16, borderRadius: "0 8px 8px 0", marginBottom: 16 }}>
                    <div className="xs" style={{ color: accentColor, fontWeight: 700 }}>CORE TRUTH</div>
                    <p className="sm" style={{ color: "#E2E8F0", fontStyle: "italic", margin: 0 }}>"{SCENE.steps.step5_upgrade.coreTruth}"</p>
                  </div>
                  <div className="xs" style={{ color: mutedColor, marginBottom: 8 }}>KEY INSIGHTS:</div>
                  <ul className="sm" style={{ color: "#E2E8F0", paddingLeft: 20 }}>
                    {SCENE.steps.step5_upgrade.coachSummary.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                </div>
              )}
               {activeTab === "spk" && (
                <div className="animate-in">
                  <div className="xs" style={{ color: "#FCA5A5", fontWeight: 700, marginBottom: 12 }}>COACH TO PARENT:</div>
                  <p className="sm" style={{ color: "#E2E8F0", marginBottom: 16 }}>"{SCENE.steps.step5_upgrade.oneOnOne.speaker.hardTruth}"</p>
                  <div style={{ background: "#334155", padding: 16, borderRadius: 8 }}>
                    <div className="xs" style={{ color: mutedColor }}>FOCUS SHIFT:</div>
                    <div className="sm" style={{ color: "#fff" }}>"{SCENE.steps.step5_upgrade.oneOnOne.speaker.focus}"</div>
                  </div>
                </div>
              )}
               {activeTab === "lis" && (
                <div className="animate-in">
                  <div className="xs" style={{ color: "#93C5FD", fontWeight: 700, marginBottom: 12 }}>COACH TO CHILD:</div>
                  <p className="sm" style={{ color: "#E2E8F0", marginBottom: 16 }}>"{SCENE.steps.step5_upgrade.oneOnOne.listener.hardTruth}"</p>
                  <div style={{ background: "#334155", padding: 16, borderRadius: 8 }}>
                    <div className="xs" style={{ color: mutedColor }}>REFRAME:</div>
                    <div className="sm" style={{ color: "#fff" }}>"{SCENE.steps.step5_upgrade.oneOnOne.listener.focus}"</div>
                  </div>
                </div>
              )}
              {activeTab === "plan" && (
                <div className="animate-in">
                   <div className="xs" style={{ color: accentColor, fontWeight: 700, marginBottom: 12 }}>NEW ENVIRONMENT DESIGN</div>
                   
                   {/* The Motion Sensor Trash Can */}
                   <div style={{ background: "rgba(16, 185, 129, 0.1)", border: "1px solid #10B981", padding: 12, borderRadius: 8, marginBottom: 12 }}>
                      <div className="xs" style={{ color: "#10B981", fontWeight: 700 }}>HARDWARE UPGRADE:</div>
                      <div className="sm" style={{ color: "#D1FAE5" }}>{SCENE.steps.step5_upgrade.togetherPlan.environmentChange}</div>
                   </div>

                   {/* The Rule */}
                   <div style={{ background: "rgba(239, 68, 68, 0.1)", border: "1px solid #EF4444", padding: 12, borderRadius: 8, marginBottom: 12 }}>
                      <div className="xs" style={{ color: "#EF4444", fontWeight: 700 }}>AUTOMATIC RULE:</div>
                      <div className="sm" style={{ color: "#FECACA" }}>
                        {SCENE.steps.step5_upgrade.togetherPlan.rule} <br/>
                        <span style={{opacity: 0.8, fontSize: '0.85em'}}>Outcome: {SCENE.steps.step5_upgrade.togetherPlan.outcome}</span>
                      </div>
                   </div>

                   <button onClick={reset} style={{ width: "100%", background: accentColor, color: "#0E7490", border: "none", padding: "12px", borderRadius: 8, fontWeight: 700, marginTop: 16, cursor: "pointer" }}>Commit & Reset</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
       <div style={{ height: 4, background: "#334155", width: "100%" }}>
        <div style={{ height: "100%", background: step === 5 ? accentColor : "#F59E0B", width: `${(step / 5) * 100}%`, transition: "width 0.3s ease" }} />
      </div>
    </section>
  );
}
